import { Spacing, Typography } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { Environment } from 'dashboard/models/environment';
import { User } from 'dashboard/models/user';
import { useListCasesQuery } from 'dashboard/redux/api/case';
import Badge from 'design-system/components/Badge';
import Card from 'design-system/components/Card';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';

// [chris] todo: Remove this once we have a better pagination strategy for the module.
const DEFAULT_PAGINATION_PARAMS = {
  number: 1,
  size: 200,
};

const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const CaseCountBadge = styled(Badge)`
  margin-left: ${Spacing[2]};
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[2]} ${Spacing[1]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLink = styled(Link)`
  ${Typography.TextMediumLink};
`;

const CaseRow = styled.div`
  margin-top: ${Spacing[2]};
  padding-bottom: ${Spacing[2]};
`;

export interface RelatedCasesProps {
  accounts: Account[];
  currentEnvironment: Environment;
  hiddenCase?: Case;
  users: User[];
}

const RelatedCases = ({ hiddenCase, accounts, currentEnvironment, users }: RelatedCasesProps) => {
  const {
    data: casesResponse,
    isLoading,
    error,
  } = useListCasesQuery(
    {
      filter: {
        'environment-id': currentEnvironment.id,
        accountIds: accounts.map((account) => account.id),
      },
      page: DEFAULT_PAGINATION_PARAMS,
    },
    { refetchOnMountOrArgChange: true },
  );
  useRTKErrorHandler({
    error,
    onError: (err) => {
      if (err.status === 404) {
        return;
      }

      return {
        message: 'Failed to load related cases.',
        level: MessageType.Failure,
      };
    },
  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const { data: relatedCases } = casesResponse || { data: [] };

  // filter out the current Case from the list
  const relevantCases = relatedCases.filter((kase) => kase.id !== hiddenCase?.id);

  return (
    <PureRelatedCasesModule
      accounts={accounts}
      hiddenCase={hiddenCase}
      relatedCases={relevantCases}
      users={users}
    />
  );
};

export interface PureRelatedCasesModuleProps {
  accounts: Account[];
  hiddenCase?: Case;
  relatedCases: Case[];
  users: User[];
}

export const PureRelatedCasesModule = ({
  hiddenCase,
  relatedCases,
  accounts,
  users,
}: PureRelatedCasesModuleProps) => {
  const accountAccordions = useMemo(
    () =>
      accounts.map((account) => {
        const relevantCases = relatedCases.filter((kase) => {
          const accountIds = kase.relationships.accounts.data.map((account) => account.id);

          // filter out the current Case from the list
          return kase.id !== hiddenCase?.id && accountIds.includes(account.id);
        });

        const casesRows = relevantCases.map((kase) => {
          const assignee = users.find((user) => user.id === kase.relationships.assignee?.data?.id);
          const assigneeName = userDisplayName(assignee) || 'Unassigned';

          return (
            <CaseRow key={kase.id}>
              <NameContainer>
                <StyledLink to={`/dashboard/cases/${kase.id}`}>{kase.attributes.name}</StyledLink>
                <CaseStatusSticker caseState={kase.attributes.state} />
              </NameContainer>
              <Paragraph color='tertiary' size='sm'>
                {assigneeName}
              </Paragraph>
            </CaseRow>
          );
        });

        const nameFull =
          account.attributes['name-full-localized'] ?? account.attributes['name-full'];

        return (
          <Accordion
            key={account.id}
            title={
              <TitleContainer>
                <Header>
                  {nameFull ? (
                    <Label color='primary' size='lg' weight='bold'>
                      {nameFull}
                    </Label>
                  ) : (
                    <Label color='tertiary' size='lg' weight='bold'>
                      No name collected
                    </Label>
                  )}
                  <CaseCountBadge>{relevantCases.length}</CaseCountBadge>
                </Header>
                <StyledLink to={`/dashboard/accounts/${account.id}`}>{account.id}</StyledLink>
              </TitleContainer>
            }
            defaultExpanded={true}
            menuItem={true}
            menuItemHover={false}
            padded={false}
          >
            {casesRows}
          </Accordion>
        );
      }),
    [relatedCases, accounts, users],
  );

  return (
    <Card>
      <Card.Body>
        <Header>
          <Heading size='sm'>Related cases</Heading>
        </Header>
        {relatedCases.length > 0 ? (
          <>{accountAccordions}</>
        ) : (
          <Paragraph color='tertiary'>There are no related cases.</Paragraph>
        )}
      </Card.Body>
    </Card>
  );
};

export default RelatedCases;
